import { createFileRoute } from '@tanstack/react-router'
import { useMemo } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import type { BotStatus } from '@/domains/admin/api'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAdminBotsQuery, useAdminStatsQuery } from '@/domains/admin/queries'
import { USDCToString } from '@/lib/currency'

export const Route = createFileRoute('/admin/')({
  component: AdminDashboard,
})

const columnHelper = createColumnHelper<BotStatus>()
const EMPTY_BOTS: BotStatus[] = []

function AdminDashboard() {
  const { data: bots, isLoading } = useAdminBotsQuery()
  const { data: stats, isLoading: statsLoading } = useAdminStatsQuery()

  const columns = useMemo(
    () => [
      columnHelper.accessor('username', {
        header: 'Bot',
        cell: ({ row }) => (
          <span className="font-medium">{row.original.username}</span>
        ),
      }),
      columnHelper.accessor('initial_balance', {
        header: 'Initial',
        cell: ({ row }) => (
          <span>${USDCToString(row.original.initial_balance)}</span>
        ),
      }),
      columnHelper.accessor('balance', {
        header: 'Balance',
        cell: ({ row }) => <span>${USDCToString(row.original.balance)}</span>,
      }),
      columnHelper.accessor('active_stack', {
        header: 'In Play',
        cell: ({ row }) => (
          <span>${USDCToString(row.original.active_stack)}</span>
        ),
      }),
      columnHelper.accessor('profit_loss', {
        header: 'P/L',
        cell: ({ row }) => {
          const pl = row.original.profit_loss
          const isUp = row.original.is_up
          return (
            <span className={isUp ? 'text-green-600' : 'text-red-600'}>
              {isUp ? '+' : ''}${USDCToString(pl)}
            </span>
          )
        },
      }),
      columnHelper.accessor('is_up', {
        header: 'Status',
        cell: ({ row }) => (
          <Badge variant={row.original.is_up ? 'default' : 'destructive'}>
            {row.original.is_up ? 'Up' : 'Down'}
          </Badge>
        ),
      }),
    ],
    [],
  )

  const table = useReactTable({
    data: bots ?? EMPTY_BOTS,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h2 className="text-3xl font-medium tracking-tight">
          Admin Dashboard
        </h2>
        <p className="text-muted-foreground">
          Bot balances and performance relative to their initial seed.
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Total Rake Collected</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-2xl font-semibold">
            {statsLoading
              ? 'Loading...'
              : `$${USDCToString(stats?.total_rake_collected ?? 0)}`}
          </p>
        </CardContent>
      </Card>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="cursor-default">
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="cursor-default">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="h-24 text-center"
              >
                No bots found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
